import { useState, useEffect } from 'react';
import { solarMonitoringAPI } from '../services/api';
import { AlertRule, AlertHistory, MonitoringStatus, AlertRuleFormData } from '../types/solarMonitoring';

export const useSolarMonitoring = () => {
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [alertHistory, setAlertHistory] = useState<AlertHistory[]>([]);
  const [monitoringStatus, setMonitoringStatus] = useState<MonitoringStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [testEmailLoading, setTestEmailLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAlertRules(),
        fetchAlertHistory(),
        fetchMonitoringStatus()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertRules = async () => {
    try {
      const data = await solarMonitoringAPI.getAlertRules();
      setAlertRules(data.data);
    } catch (error) {
      console.error('Error fetching alert rules:', error);
    }
  };

  const fetchAlertHistory = async () => {
    try {
      const data = await solarMonitoringAPI.getAlertHistory({ limit: 20 });
      setAlertHistory(data.data);
    } catch (error) {
      console.error('Error fetching alert history:', error);
    }
  };

  const fetchMonitoringStatus = async () => {
    try {
      const data = await solarMonitoringAPI.getMonitoringStatus();
      setMonitoringStatus(data.data);
    } catch (error) {
      console.error('Error fetching monitoring status:', error);
    }
  };

  const createAlertRule = async (ruleData: AlertRuleFormData) => {
    try {
      await solarMonitoringAPI.createAlertRule(ruleData);
      await fetchAlertRules();
    } catch (error) {
      console.error('Error creating alert rule:', error);
      throw error;
    }
  };

  const updateAlertRule = async (id: string, ruleData: AlertRuleFormData) => {
    try {
      await solarMonitoringAPI.updateAlertRule(id, ruleData);
      await fetchAlertRules();
    } catch (error) {
      console.error('Error updating alert rule:', error);
      throw error;
    }
  };

  const deleteAlertRule = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this alert rule?')) return;

    try {
      await solarMonitoringAPI.deleteAlertRule(id);
      await fetchAlertRules();
    } catch (error) {
      console.error('Error deleting alert rule:', error);
      throw error;
    }
  };

  const toggleAlertRule = async (id: string) => {
    try {
      await solarMonitoringAPI.toggleAlertRule(id);
      await fetchAlertRules();
    } catch (error) {
      console.error('Error toggling alert rule:', error);
      throw error;
    }
  };

  const testAlertEmail = async (emails: string[]) => {
    if (emails.length === 0) {
      alert('Please add at least one email address');
      return;
    }

    setTestEmailLoading(true);
    try {
      await solarMonitoringAPI.testAlertEmail(emails);
      alert('Test email sent successfully!');
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Error sending test email');
    } finally {
      setTestEmailLoading(false);
    }
  };

  return {
    // State
    alertRules,
    alertHistory,
    monitoringStatus,
    loading,
    testEmailLoading,
    
    // Actions
    createAlertRule,
    updateAlertRule,
    deleteAlertRule,
    toggleAlertRule,
    testAlertEmail,
    fetchData
  };
};